'use client';

import { useState, ReactNode, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search, User, ShoppingCart, Menu, ChevronDown } from 'lucide-react';
import { HttpTypes } from '@medusajs/types';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { JinMobileMenu } from './jin-mobile-menu';
import { useTenant, useTenantAndVariant } from '@multitenancy/TenantAndVariantContext';

interface HeaderShowcaseProps {
  regions: HttpTypes.StoreRegion[];
  collections: HttpTypes.StoreCollection[];
  categories: HttpTypes.StoreProductCategory[];
  cartButton: ReactNode;
}

function findCategory(categories: HttpTypes.StoreProductCategory[], name: string) {
  return categories.find(c => c.name.toLowerCase() === name.toLowerCase());
}

function buildAccessoriesData(categories: HttpTypes.StoreProductCategory[]) {
  const jewellery = findCategory(categories, 'Jewellery');
  if (!jewellery) return null;
  return {
    parent: jewellery,
    children: (jewellery.category_children || []).map((child: any) => ({
      ...child,
      children: child.category_children || [],
    })),
  };
}

function buildClothingsData(categories: HttpTypes.StoreProductCategory[]) {
  const clothing = findCategory(categories, 'Clothing');
  if (!clothing) return null;
  return {
    parent: clothing,
    children: clothing.category_children || [],
  };
}

export function HeaderShowcase({ regions, collections, categories, cartButton }: HeaderShowcaseProps) {
  const storeConfig = useTenant();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { featureToggles } = useTenantAndVariant();

  const accessories = useMemo(() => buildAccessoriesData(categories), [categories]);
  const clothings = useMemo(() => buildClothingsData(categories), [categories]);

  return (
    <>
      <header className="sticky top-0 z-50 bg-white">
        {/* 1. First Announcement Bar */}
        {featureToggles.showAnnouncementBar1 && (
          <div className="bg-primary text-white text-center py-2 text-xs tracking-wider font-medium">
            <p>Welcome to {storeConfig.name}</p>
          </div>
        )}

        {/* 2. Second Announcement Bar */}
        {featureToggles.showAnnouncementBar2 && (
          <div className="bg-secondary text-foreground text-center py-2 text-xs tracking-wider font-medium">
            <p>Don&apos;t Miss Our Grand Opening Special! Shop Now for Great Deals!</p>
          </div>
        )}

        {/* 3. Third Announcement Bar */}
        {featureToggles.showAnnouncementBar3 && (
          <div className="bg-tertiary text-foreground text-center py-2 text-xs tracking-wider font-medium">
            <p>Free shipping on orders $150+</p>
          </div>
        )}

        {/* 2. Logo and Icons Bar */}
        <div className="border-b border-gray-200 bg-white">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            {/* Mobile Menu Button (left side on mobile) */}
            <div className="flex-1 lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 hover:text-[var(--primary)] transition-colors"
                aria-label="Open menu"
              >
                <Menu size={24} />
              </button>
            </div>

            {/* Empty space for alignment on desktop */}
            <div className="hidden lg:block flex-1"></div>

            {/* Centered Logo */}
            <LocalizedClientLink href="/" className="flex items-center justify-center">
              <Image
                src={`/images/logo_${storeConfig.name}_horizontal.png`}
                alt={storeConfig.displayName.toUpperCase()}
                width={200}
                height={60}
                className="h-12 md:h-16 w-auto"
                priority
              />
            </LocalizedClientLink>

            {/* Icons on the right */}
            <div className="flex-1 flex items-center justify-end space-x-4 md:space-x-6">
              <LocalizedClientLink
                href="/search"
                aria-label="Search"
                className="hover:text-[var(--primary)] transition-colors"
                scroll={false}
              >
                <Search size={20} />
              </LocalizedClientLink>
              <LocalizedClientLink
                href="/account"
                aria-label="Account"
                className="hover:text-[var(--primary)] transition-colors hidden sm:block"
              >
                <User size={20} />
              </LocalizedClientLink>
              {/* Real Cart Button with server-side data */}
              {cartButton}
            </div>
          </div>
        </div>

        {/* 3. Navigation Bar: Home | New Arrivals | Accessories ▼ | Clothings ▼ | Shop All */}
        <div className="border-b border-gray-200 bg-white">
          <nav className="container mx-auto px-4 py-3 flex items-center justify-center">
            <div className="hidden lg:flex items-center space-x-8 text-sm font-bold tracking-widest uppercase text-[var(--muted)]">
              <LocalizedClientLink href="/" className="hover:text-[var(--foreground)] transition-colors">
                Home
              </LocalizedClientLink>

              {/* TODO: "New Arrivals" is a Shopify collection on the source site, not a category.
                  Currently linking to /store as a temporary measure until we set up a proper
                  "New Arrivals" collection in Medusa. Original source path was /categories/black-friday-christmas-day */}
              <LocalizedClientLink href="/store" className="hover:text-[var(--foreground)] transition-colors">
                New Arrivals
              </LocalizedClientLink>

              {/* Accessories Mega-Menu (from Jewellery category) */}
              {accessories && (
                <div className="relative group">
                  <button className="hover:text-[var(--foreground)] transition-colors flex items-center gap-1">
                    Accessories
                    <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                  </button>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 shadow-xl rounded-md py-6 px-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[700px]">
                    <LocalizedClientLink
                      href={`/categories/${accessories.parent.handle}`}
                      className="block mb-4 text-xs font-bold tracking-widest uppercase text-[var(--primary)] hover:underline"
                    >
                      All Accessories
                    </LocalizedClientLink>
                    <div className="grid grid-cols-4 gap-6">
                      {accessories.children.map((subGroup: any) => (
                        <div key={subGroup.id}>
                          <LocalizedClientLink
                            href={`/categories/${subGroup.handle}`}
                            className="block text-xs font-bold tracking-wider uppercase text-[var(--foreground)] mb-2 hover:text-[var(--primary)] transition-colors"
                          >
                            {subGroup.name}
                          </LocalizedClientLink>
                          {subGroup.children && subGroup.children.length > 0 && (
                            <ul className="space-y-1.5">
                              <li>
                                <LocalizedClientLink
                                  href={`/categories/${subGroup.handle}`}
                                  className="text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                >
                                  All {subGroup.name}
                                </LocalizedClientLink>
                              </li>
                              {subGroup.children.map((child: any) => (
                                <li key={child.id}>
                                  <LocalizedClientLink
                                    href={`/categories/${child.handle}`}
                                    className="text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                  >
                                    {child.name}
                                  </LocalizedClientLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Clothings Dropdown (from Clothing category) */}
              {clothings && (
                <div className="relative group">
                  <button className="hover:text-[var(--foreground)] transition-colors flex items-center gap-1">
                    Clothings
                    <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                  </button>
                  <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 shadow-lg rounded-md py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <LocalizedClientLink
                      href={`/categories/${clothings.parent.handle}`}
                      className="block px-4 py-2 text-xs font-bold tracking-wider uppercase text-[var(--primary)] hover:bg-gray-50 transition-colors"
                    >
                      All Clothings
                    </LocalizedClientLink>
                    {clothings.children.map((child: any) => (
                      <LocalizedClientLink
                        key={child.id}
                        href={`/categories/${child.handle}`}
                        className="block px-4 py-2 text-xs font-semibold tracking-wider hover:bg-gray-50 hover:text-[var(--foreground)] transition-colors"
                      >
                        {child.name}
                      </LocalizedClientLink>
                    ))}
                  </div>
                </div>
              )}

              <LocalizedClientLink href="/store" className="hover:text-[var(--foreground)] transition-colors">
                Shop All
              </LocalizedClientLink>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <JinMobileMenu
        regions={regions}
        collections={collections}
        categories={categories}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </>
  );
}
